/**
 * Detects Schedule nodes that are wired into the call flow but have no schedule
 * selected (VOI-665).
 *
 * Such a node persists as `"schedule_id": null`. FreeSWITCH cannot resolve
 * open/closed from that, so it routes every caller down the **Closed** branch —
 * during business hours — and drops the call outright when Closed is not
 * connected either. It is silent: nothing surfaces to the customer.
 *
 * Only nodes reachable from the start node can affect a call. Disconnected
 * leftover Schedule boxes are common (128 of 140 unset nodes in production when
 * this was written) and are deliberately ignored: blocking on those would make
 * roughly 89 existing switchboards unsaveable for reasons the editor's user did
 * not create.
 */
import { NodeType } from '../converters/shared/reactFlowTypes'
import type {
  SwitchboardFlowData,
  SwitchboardNode,
} from '../converters/shared/reactFlowTypes'

export interface UnconfiguredScheduleNode {
  nodeId: string
}

/**
 * Values that mean "no schedule picked". `'0'` and `0` matter because
 * `switchboard/schedule.lua` substitutes the literal id 0 for a missing value
 * and then requests `schedules/0/`, which can only 404. `'null'` appears when a
 * JSON null has been round-tripped through `String()`.
 */
const UNSET_SCHEDULE_IDS = new Set(['', '0', 'null', 'undefined'])

function willBeSaved(node: SwitchboardNode): boolean {
  if (!node.type || !node.id) {
    return false
  }

  return !(
    node.data.deleted === true || node.data.shouldExcludeFromSave === true
  )
}

function hasNoScheduleSelected(node: SwitchboardNode): boolean {
  const scheduleId = (node.data as { scheduleId?: unknown }).scheduleId

  if (scheduleId === undefined || scheduleId === null) {
    return true
  }

  if (typeof scheduleId === 'number') {
    return scheduleId === 0
  }

  return UNSET_SCHEDULE_IDS.has(String(scheduleId).trim())
}

/**
 * Returns every Schedule node that a call can reach and that has no schedule
 * selected. Empty when the flow is fine, when the offending nodes are all
 * disconnected, or when there is no start node at all — a malformed graph must
 * never make a switchboard unsaveable.
 */
export function findUnconfiguredConnectedScheduleNodes(
  flowData: SwitchboardFlowData,
): UnconfiguredScheduleNode[] {
  const saveableNodes = flowData.nodes.filter(willBeSaved)
  const nodeById = new Map(saveableNodes.map((node) => [node.id, node]))

  const startNode = saveableNodes.find((node) => node.type === 'start')
  if (!startNode) {
    return []
  }

  const targetsBySource = new Map<string, string[]>()
  flowData.edges.forEach((edge) => {
    // An edge through a node that will not be saved is not a route a call can
    // take, so it must not make anything downstream look reachable.
    if (!nodeById.has(edge.source) || !nodeById.has(edge.target)) {
      return
    }

    const targets = targetsBySource.get(edge.source)
    if (targets) {
      targets.push(edge.target)
      return
    }

    targetsBySource.set(edge.source, [edge.target])
  })

  const reachable = new Set<string>()
  const pending = [startNode.id]
  while (pending.length > 0) {
    const current = pending.pop() as string
    if (reachable.has(current)) {
      continue
    }

    reachable.add(current)
    targetsBySource.get(current)?.forEach((target) => {
      if (!reachable.has(target)) {
        pending.push(target)
      }
    })
  }

  return saveableNodes
    .filter(
      (node) =>
        node.type === NodeType.Schedule &&
        reachable.has(node.id) &&
        hasNoScheduleSelected(node),
    )
    .map((node) => ({ nodeId: node.id }))
}
